import Icon from "./Icon.jsx";


/**
 * Badge component
 * 
 * Renders a badge component with a dynamic size and color.
 * The size can be either 'sm' or 'md', and the color can be one of the following values:
 * - dull_lavender
 * - curious_blue
 * - fun_green
 * - pizza
 * - vivid
 * - oslo
 * - coral
 * - anakiwa
 * - cerulean_blue
 * - vital_green
 * - vital_orange
 * - vital_red
 * 
 * If no size or color is provided, the component defaults to 'md' and 'dull_lavender' respectively.
 * 
 * @param {{color?: string, size?: 'sm' | 'md', showIcon?: boolean, text?: string, fullHeight?: boolean, extraClasses?: string, iconName?: string, children?: React.ReactNode}} props
 * @param {string} [props.iconName] - The icon to display in badge.
 * @param {string} [props.color='dull_lavender'] - The color of the badge.
 * @param {'sm' | 'md'} [props.size='md'] - The size of the badge.
 * @param {string} [props.text='Badge'] - The text content displayed inside the badge component.
 * @param {boolean} [props.fullHeight=false] - If true, the badge will expand to the height of its parent.
 * @param {string} [props.extraClasses] - Additional classes to apply to the badge container.
 */
export default function Badge({
    color,
    children,
    iconName,
    size = 'md',
    text,
    fullHeight = false,
    extraClasses,
    ...rest
}) {

    const SIZE_CLASS_MAP = {
        'sm': {
            heightClass: 'h-6',
            textClass: 'text-caption',
            iconSize: 'xs',
        },
        'md': {
            heightClass: 'h-[28px]',
            textClass: 'text-body-small',
            iconSize: 'sm',
        }
    };

    const COLOR_MAP = {
        'dull_lavender': {
            backgroundColor: 'bg-dull-lavender-100',
            textColor: 'text-dull-lavender-700',
        },
        'curious_blue': {
            backgroundColor: 'bg-curious-blue-100',
            textColor: 'text-curious-blue-700',
        },
        'fun_green': {
            backgroundColor: 'bg-fun-green-100',
            textColor: 'text-fun-green-700',
        },
        'pizza': {
            backgroundColor: 'bg-pizza-100',
            textColor: 'text-pizza-700',
        },
        'vivid': {
            backgroundColor: 'bg-vivid-tangerine-100',
            textColor: 'text-vivid-tangerine-700',
        },
        'oslo': {
            backgroundColor: 'bg-oslo-gray-100',
            textColor: 'text-oslo-gray-700',
        },
        'coral': {
            backgroundColor: 'bg-coral-100',
            textColor: 'text-coral-700',
        },
        'anakiwa': {
            backgroundColor: 'bg-anakiwa-100',
            textColor: 'text-anakiwa-700',
        },
        'cerulean_blue': {
            backgroundColor: 'bg-cerulean-blue-100',
            textColor: 'text-cerulean-blue-700',
        },
        'vital_green': {
            backgroundColor: 'bg-vital-green-100',
            textColor: 'text-vital-green-700',
        },
        'vital_orange': {
            backgroundColor: 'bg-vital-orange-100',
            textColor: 'text-vital-orange-700',
        },
        'vital_red': {
            backgroundColor: 'bg-vital-red-100',
            textColor: 'text-vital-red-700',
        },
        'tertiary': {
            backgroundColor: 'bg-main-border-accented-tertiary',
            textColor: 'text-main-typography-accented-tertiary',
        },
    }

    const baseClasses =
        'inline-flex items-center justify-center px-3 py-2 font-normal whitespace-nowrap rounded-lg';

    function buildIcon({ iconName, iconSize, className }) {
        if (!iconName) return null;
        return <span
            className="ml-1 inline-flex items-center justify-center p-0 m-0"

        >
            <Icon name={iconName} size={iconSize} className={className} />
        </span>

    }
    const sizeConfig = SIZE_CLASS_MAP[size] || SIZE_CLASS_MAP['md'];
    const colorConfig = COLOR_MAP[color] || COLOR_MAP['dull_lavender'];

    const heightClass = fullHeight ? 'h-full' : sizeConfig.heightClass;
    const allClasses = `${baseClasses} ${heightClass} ${sizeConfig.textClass} ${colorConfig.backgroundColor} ${colorConfig.textColor} ${extraClasses}`;

    return (
        <span className={allClasses} {...rest}>
            {iconName && (
                buildIcon({ iconName, iconSize: sizeConfig.iconSize, className: colorConfig.textColor })
            )}
            {children || (text && <span className="px-2">{text}</span>)}
        </span>
    );
};
